/**
 * ParameterValueSourceTabs
 *
 * Shared two-tab strip used by the field-anchored fork popover and the
 * left input rail. Lets the user switch between picking dynamic content
 * (tokens) and writing a workflow expression.
 *
 * This is a pure presentational component — it owns no state. The
 * consumer drives the selected tab and renders the per-tab content as
 * `children`.
 */

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@coreai/fluentui-react';
import {
  BracesVariableRegular,
  MathFormulaRegular,
} from '@fluentui/react-icons';

export type ValueSource = 'tokens' | 'expr';

export interface ParameterValueSourceTabsProps {
  /** Currently selected tab. */
  selected: ValueSource;
  /** Fired when the user activates a tab. */
  onSelect: (source: ValueSource) => void;
  /**
   * Optional rendered slot below the tabs — same content for either tab;
   * the tab choice is just a UI signal here. Most consumers will render
   * different children based on `selected`.
   */
  children?: ReactNode;
  /** When true, tab strip sticks to the top of the parent scroll container. */
  sticky?: boolean;
  /** Optional className passthrough for the outer wrapper. */
  className?: string;
}

export interface ParameterValueSourceTabsHandle {
  /** Move keyboard focus to the currently selected tab. */
  focus: () => void;
}

const TAB_ORDER: ValueSource[] = ['tokens', 'expr'];

const TAB_LABELS: Record<ValueSource, string> = {
  tokens: 'Dynamic content',
  expr: 'Expressions',
};

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  sticky: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    // Match the surrounding panel surface (Background2) so the strip
    // visually merges with the body instead of reading as a separate
    // white card stacked on top of it.
    backgroundColor: tokens.colorNeutralBackground2,
  },
  tablist: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: tokens.spacingHorizontalS,
    minHeight: '40px',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    cursor: 'pointer',
    transitionProperty: 'color, background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
      // Tab strip sits on Background2 — use the matching hover token so
      // hovered tabs don't flash brighter than their surrounding surface.
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  tabSelected: {
    color: tokens.colorBrandForeground1,
    borderBottomColor: tokens.colorBrandStroke1,
    ':hover': {
      color: tokens.colorBrandForeground1,
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    '@media (forced-colors: active)': {
      borderBottomColor: 'Highlight',
      color: 'Highlight',
    },
  },
  glyph: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
    },
  },
  glyphSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'Canvas',
      color: 'Highlight',
      border: '1px solid Highlight',
    },
  },
  // Per-tab tint variants applied on top of glyphSelected so each tab
  // mirrors its corresponding FieldActionChip:
  //   tokens   → brand blue (matches the + / BracesVariable chip)
  //   expr     → teal       (matches the fx / MathFormula chip)
  glyphSelectedTokens: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  glyphSelectedExpr: {
    backgroundColor: tokens.colorPaletteTealBackground2,
    color: tokens.colorPaletteTealForeground2,
    '@media (forced-colors: active)': {
      backgroundColor: 'Canvas',
      color: 'Highlight',
      border: '1px solid Highlight',
    },
  },
  // Selected-label color also follows the chip — keeps the whole tab
  // chrome (label + glyph + bottom indicator) in one tint.
  tabSelectedTokens: {
    color: tokens.colorBrandForeground1,
    borderBottomColor: tokens.colorBrandStroke1,
  },
  tabSelectedExpr: {
    color: tokens.colorPaletteTealForeground2,
    borderBottomColor: tokens.colorPaletteTealBorderActive,
  },
  label: {
    whiteSpace: 'nowrap',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },
});

/**
 * Two-tab strip implementing the W3C tabs pattern.
 */
export const ParameterValueSourceTabs = forwardRef<
  ParameterValueSourceTabsHandle,
  ParameterValueSourceTabsProps
>(({ selected, onSelect, children, sticky = false, className }, ref) => {
  const styles = useStyles();
  const tabRefs = useRef<Record<ValueSource, HTMLButtonElement | null>>({
    tokens: null,
    expr: null,
  });

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        tabRefs.current[selected]?.focus();
      },
    }),
    [selected],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, source: ValueSource) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const currentIndex = TAB_ORDER.indexOf(source);
        const nextIndex =
          event.key === 'ArrowRight'
            ? (currentIndex + 1) % TAB_ORDER.length
            : (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
        const next = TAB_ORDER[nextIndex];
        if (next) {
          onSelect(next);
          tabRefs.current[next]?.focus();
        }
      } else if (event.key === 'Home') {
        event.preventDefault();
        const first = TAB_ORDER[0];
        if (first) {
          onSelect(first);
          tabRefs.current[first]?.focus();
        }
      } else if (event.key === 'End') {
        event.preventDefault();
        const last = TAB_ORDER[TAB_ORDER.length - 1];
        if (last) {
          onSelect(last);
          tabRefs.current[last]?.focus();
        }
      }
    },
    [onSelect],
  );

  const renderTab = (source: ValueSource) => {
    const isSelected = selected === source;
    const isExpr = source === 'expr';
    // The tabpanel is only rendered for the selected source, and only when a
    // panel (children) is supplied. Point aria-controls at it solely in that
    // case — referencing an absent id fails aria-valid-attr-value.
    const controlsPanel = isSelected && children !== undefined;
    return (
      <button
        key={source}
        ref={(el) => {
          tabRefs.current[source] = el;
        }}
        type="button"
        role="tab"
        id={`value-source-tab-${source}`}
        aria-selected={isSelected}
        {...(controlsPanel ? { 'aria-controls': `value-source-panel-${source}` } : {})}
        tabIndex={isSelected ? 0 : -1}
        className={mergeClasses(
          styles.tab,
          isSelected && styles.tabSelected,
          // Per-tab tint on top of the base selected style so the label
          // colour + bottom indicator + glyph all share the same hue
          // as the corresponding FieldActionChip (brand for tokens,
          // teal for expressions).
          isSelected && (isExpr ? styles.tabSelectedExpr : styles.tabSelectedTokens),
        )}
        onClick={() => onSelect(source)}
        onKeyDown={(e) => handleKeyDown(e, source)}
      >
        <span
          aria-hidden="true"
          className={mergeClasses(
            styles.glyph,
            isSelected && styles.glyphSelected,
            isSelected && (isExpr ? styles.glyphSelectedExpr : styles.glyphSelectedTokens),
          )}
        >
          {isExpr ? <MathFormulaRegular /> : <BracesVariableRegular />}
        </span>
        <span className={styles.label}>{TAB_LABELS[source]}</span>
      </button>
    );
  };

  return (
    <div className={mergeClasses(styles.wrapper, className)}>
      <div
        role="tablist"
        aria-label="Parameter value source"
        aria-orientation="horizontal"
        className={mergeClasses(styles.tablist, sticky && styles.sticky)}
      >
        {TAB_ORDER.map(renderTab)}
      </div>
      {children !== undefined && (
        <div
          role="tabpanel"
          id={`value-source-panel-${selected}`}
          aria-labelledby={`value-source-tab-${selected}`}
          className={styles.body}
        >
          {children}
        </div>
      )}
    </div>
  );
});

ParameterValueSourceTabs.displayName = 'ParameterValueSourceTabs';
